import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { AbsenceDateRange } from "../attendance.types";

type CellPointerEvent = ReactPointerEvent<HTMLButtonElement>;

function orderedRange(first: string, second: string): AbsenceDateRange {
  return first <= second
    ? { startDate: first, endDate: second }
    : { startDate: second, endDate: first };
}

export function useDateDragSelection({
  dates,
  isDateSelectable,
  onSelect,
}: {
  dates: string[];
  isDateSelectable: (date: string) => boolean;
  onSelect: (range: AbsenceDateRange) => void;
}) {
  const [selection, setSelection] = useState<AbsenceDateRange | null>(null);
  const anchorRef = useRef<string | null>(null);
  const suppressNextClickRef = useRef(false);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setSelection(null);
  }, []);

  useEffect(() => {
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
      window.removeEventListener("blur", clear);
    };
  }, [clear]);

  const isRangeSelectable = useCallback(
    (first: string, second: string) => {
      const firstIndex = dates.indexOf(first);
      const secondIndex = dates.indexOf(second);
      if (firstIndex < 0 || secondIndex < 0) return false;
      const from = Math.min(firstIndex, secondIndex);
      const to = Math.max(firstIndex, secondIndex);
      return dates.slice(from, to + 1).every(isDateSelectable);
    },
    [dates, isDateSelectable],
  );

  const onPointerDown = useCallback(
    (event: CellPointerEvent, date: string) => {
      if (
        event.pointerType !== "mouse" ||
        event.button !== 0 ||
        !isDateSelectable(date)
      ) {
        return;
      }
      anchorRef.current = date;
      setSelection(orderedRange(date, date));
    },
    [isDateSelectable],
  );

  const onPointerEnter = useCallback(
    (event: CellPointerEvent, date: string) => {
      const anchor = anchorRef.current;
      if (!anchor || event.pointerType !== "mouse" || !(event.buttons & 1)) {
        return;
      }
      setSelection(
        isRangeSelectable(anchor, date) ? orderedRange(anchor, date) : null,
      );
    },
    [isRangeSelectable],
  );

  const onPointerUp = useCallback(
    (event: CellPointerEvent, date: string) => {
      const anchor = anchorRef.current;
      if (!anchor || event.pointerType !== "mouse") return;

      const moved = anchor !== date;
      if (moved) {
        suppressNextClickRef.current = true;
        // A drag may not produce a click when it ends on a different cell. Keep suppression scoped to
        // the compatibility click from this pointer-up instead of swallowing the user's next real click.
        window.setTimeout(() => {
          suppressNextClickRef.current = false;
        }, 0);
      }
      if (moved && isRangeSelectable(anchor, date)) {
        onSelect(orderedRange(anchor, date));
      }
      clear();
    },
    [clear, isRangeSelectable, onSelect],
  );

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressNextClickRef.current) return false;
    suppressNextClickRef.current = false;
    return true;
  }, []);

  const onClick = useCallback(
    (date: string) => {
      if (consumeSuppressedClick()) return;
      onSelect(orderedRange(date, date));
    },
    [consumeSuppressedClick, onSelect],
  );

  return {
    selection,
    onPointerDown,
    onPointerEnter,
    onPointerUp,
    onClick,
    consumeSuppressedClick,
  };
}
